/* eslint-disable react/prop-types */
// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBadge from "components/ArgonBadge";
import { AdminUnreadBadge } from "components/AdminUnread";
import { Button, Rating } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useState, useEffect } from "react";
import api from "utils/api";
import dayjs from "dayjs";

function Author({ image, name, email }) {
  return (
    <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
      <ArgonBox mr={2}>
        <ArgonAvatar src={image} alt={name} name={name} size="sm" variant="rounded" />
      </ArgonBox>
      <ArgonBox display="flex" flexDirection="column">
        <ArgonTypography variant="button" fontWeight="medium">
          {name}
        </ArgonTypography>
        <ArgonTypography variant="caption" color="secondary">
          {email}
        </ArgonTypography>
      </ArgonBox>
    </ArgonBox>
  );
}

const FeedbackTableData = (filters = {}, handleViewClick = () => {}) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const params = {
        page: filters.page || 1,
        limit: filters.limit || 50,
      };

      if (filters.type) {
        params.type = filters.type;
      }

      const response = await api.get("/feedbacks", { params });
      setFeedbacks(response.data.data || []);
      setTotalCount(response.data.pagination?.total || 0);
    } catch (error) {
      console.error("Error fetching feedbacks:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, [filters]);

  const columns = [
    { name: "sno", align: "center" },
    { name: "User", align: "left" },
    { name: "Type", align: "center" },
    { name: "Rating", align: "center" },
    { name: "Description", align: "left" },
    { name: "Created By", align: "center" },
    { name: "Date", align: "center" },
    { name: "Action", align: "center" },
  ];

  const rows = feedbacks.map((feedback, index) => {
    const user = feedback.parentId || feedback.tutorId;
    const userName = user?.fullName || "N/A";
    const userEmail = user?.email || "N/A";
    const userImage = user?.profileImage || null;
    const isUnread = Boolean(feedback.adminUnread);

    return {
      sno: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {(filters.page - 1) * filters.limit + index + 1}
        </ArgonTypography>
      ),
      Type: (
        <ArgonBadge
          variant="gradient"
          badgeContent={feedback.type}
          color={feedback.type === "PARENT" ? "info" : "success"}
          size="xs"
          container
        />
      ),
      Rating: (
        <ArgonBox display="flex" alignItems="center" justifyContent="center" gap={1}>
          <Rating value={feedback.rating || 0} readOnly size="small" max={10} />
          <ArgonTypography variant="caption" fontWeight="medium">
            {feedback.rating}/10
          </ArgonTypography>
        </ArgonBox>
      ),
      Description: (
        <ArgonBox display="flex" alignItems="center" gap={0.75} sx={{ maxWidth: 200 }}>
          <ArgonTypography
            variant="caption"
            color={isUnread ? "error" : "text"}
            fontWeight={isUnread ? "bold" : "regular"}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            }}
          >
            {feedback.description}
          </ArgonTypography>
          <AdminUnreadBadge unread={isUnread} />
        </ArgonBox>
      ),
      User: <Author image={userImage} name={userName} email={userEmail} />,
      "Created By": (
        <ArgonBadge
          variant="contained"
          badgeContent={feedback.createdBy}
          color="secondary"
          size="xs"
          container
        />
      ),
      Date: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {dayjs(feedback.createdAt).format("DD MMMM YYYY")}
        </ArgonTypography>
      ),
      Action: (
        <Button
          onClick={() => handleViewClick(feedback)}
          variant="text"
          size="large"
          sx={{
            minWidth: "12px",
            p: 0,
            borderRadius: "50%",
            backgroundColor: isUnread ? "rgba(245, 54, 92, 0.1)" : "transparent",
            "&:hover": {
              backgroundColor: isUnread ? "rgba(245, 54, 92, 0.18)" : "transparent",
            },
          }}
        >
          <VisibilityIcon color={isUnread ? "error" : "primary"} />
        </Button>
      ),
    };
  });

  return { columns, rows, loading, totalCount, fetchFeedbacks };
};

export default FeedbackTableData;

